/**
 * Source control for enterprise projects: GitHub and Azure DevOps. A Jira Cloud connection is a
 * connection too, for issue trackers only (trackers.ts): no repositories, no git credential.
 *
 * Every person uses **their own credential**: a linked account per provider
 * (GitHub through the tenant's OAuth app or a personal token; Azure DevOps
 * a personal token), kept encrypted on the platform and never returned.
 * Commits are theirs, reads see what they may see. A tenant holds
 * **connections** (a provider, an organisation, optionally an organisation
 * token used only for reads when a person has not linked, and optionally
 * as a fallback credential when the tenant allows it). A project names its
 * **source**: which connection, which organisation or Azure DevOps project,
 * and which repositories, so roots reported by nodes match by remote, the
 * console can list repositories to pick from, nodes can fetch a git
 * credential per task for the person who dispatched it, and open work
 * (issues, pull requests, work items) shows on the project.
 *
 *   GET|POST /admin/v1/source-connections, PATCH|DELETE /admin/v1/source-connections/:id
 *   POST /admin/v1/source-connections/:id/check                     reach the provider
 *   GET  /source/v1/connections                                     anyone signed in: { id, name, provider, organisation, baseUrl }[]
 *   POST /source/v1/webhooks/:id/{github,azure-devops,jira}         the provider calls back, signed with the connection's secret
 *   GET  /source/v1/identities                                      the caller's linked accounts
 *   PUT  /source/v1/identities/:provider { token, connectionId? }   link with a personal token
 *   DELETE /source/v1/identities/:provider
 *   GET  /source/v1/github/connect?connection=<id>                  start GitHub OAuth (redirects)
 *   GET  /source/v1/github/callback                                 GitHub returns here; sends the browser to the console with `github_link=<ticket>`
 *   POST /source/v1/github/finish { ticket }                        the console, signed in as the person who started: links the account
 *   GET  /source/v1/connections/:id/repos?project=                  repositories (caller's credential, else the organisation's)
 *   GET|PUT /projects/v1/:id/source                                 owners write (sign-in); members read, also from a desktop
 *                                                                   or a node naming the member in x-aioe-principal
 *   GET  /projects/v1/:id/work                                      members: open issues, PRs, work items
 *   POST /projects/v1/:id/git-credential { remote, principal? }     a node holding one of the project's
 *        roots (principal = the person it works for), or a member's desktop (its own person)
 */
import { z } from "zod";
import { projectTrackerSchema } from "./trackers.js";
export const sourceProviderSchema = z.enum(["github", "azure-devops", "jira"]);
const httpsUrl = z.string().url().max(500).refine((u) => u.startsWith("https://") || u.startsWith("http://"), "an http(s) URL");
/** What the console sees of a connection: never the token. */
export const sourceConnectionSchema = z.object({
    id: z.string(),
    provider: sourceProviderSchema,
    name: z.string().min(1).max(200),
    /** GitHub: the API base (https://api.github.com, or an Enterprise server's). Azure DevOps: https://dev.azure.com or a server. Jira: the site (https://acme.atlassian.net). */
    baseUrl: z.string(),
    /** The GitHub organisation (or user), or the Azure DevOps organisation; on Jira, the site's name. */
    organisation: z.string().min(1).max(200),
    /** Whether an organisation token is held (for reads when a person has not linked). */
    hasToken: z.boolean(),
    /** Nodes may fall back to the organisation token for a person who has not linked an account. Off by default. */
    allowSharedCredential: z.boolean(),
    /** GitHub only: the OAuth app people connect through; its secret is never returned. */
    oauthClientId: z.string().optional(),
    /**
     * GitHub only: a GitHub App. People connect through it (tokens GitHub refreshes), and the
     * organisation's installation mints short-lived tokens for reads and the fallback. Its private
     * key is never returned.
     */
    appId: z.string().optional(),
    appSlug: z.string().optional(),
    /** The app's installation on the organisation, found on check. */
    installationId: z.string().optional(),
    /** Who the organisation token authenticates as, from the last check. */
    account: z.string().optional(),
    /** Whether the provider can call back: a webhook secret is set. */
    hasWebhookSecret: z.boolean().default(false),
    createdBy: z.string(),
    createdAt: z.string(),
    lastCheckedAt: z.string().optional(),
    lastError: z.string().optional(),
});
export const sourceConnectionCreateSchema = z.object({
    provider: sourceProviderSchema,
    name: z.string().min(1).max(200),
    baseUrl: httpsUrl.optional(),
    organisation: z.string().min(1).max(200),
    /** An organisation token (GitHub fine-grained or classic; Azure DevOps PAT; Jira API token), for reads. Stored encrypted. */
    token: z.string().min(8).max(4000).optional(),
    /** Jira: the Atlassian account email the API token belongs to (they go together as basic auth). */
    email: z.string().email().max(320).optional(),
    allowSharedCredential: z.boolean().default(false),
    /** GitHub: an OAuth app registered by the organisation (callback: <api>/source/v1/github/callback). */
    oauthClientId: z.string().min(1).max(200).optional(),
    oauthClientSecret: z.string().min(1).max(500).optional(),
    /** GitHub App: its id, slug (from its URL), and private key (PEM). Client id and secret go in the OAuth fields. */
    appId: z.string().min(1).max(50).optional(),
    appSlug: z.string().min(1).max(200).optional(),
    appPrivateKey: z.string().min(100).max(10_000).optional(),
    /** What the provider signs webhooks with (GitHub and Jira: the webhook secret; Azure DevOps: the service hook's basic-auth password). */
    webhookSecret: z.string().min(8).max(200).optional(),
});
export const sourceConnectionPatchSchema = sourceConnectionCreateSchema.omit({ provider: true }).partial();
/** A person's linked account at a provider, as they see it: never the token. */
export const sourceIdentitySchema = z.object({
    provider: sourceProviderSchema,
    connectionId: z.string().optional(),
    /** The login or display name the token authenticates as. */
    account: z.string(),
    /**
     * The provider's own id for the account where `account` is only a display name (14 October 2026):
     * Azure DevOps' `uniqueName` (the UPN or email `System.AssignedTo` takes), Jira's `accountId`.
     * Absent on GitHub (the login is `account`) and on links made before it was kept.
     */
    accountId: z.string().optional(),
    /** How it was linked: through the GitHub App (`app`, refreshed by GitHub), an OAuth app, or a pasted token. */
    via: z.enum(["app", "oauth", "token"]),
    linkedAt: z.string(),
    expiresAt: z.string().optional(),
});
/**
 * Finishing a GitHub sign-in (0.23.0): the callback no longer links on its own; it hands the browser a
 * one-time ticket, and the console posts it back as the signed-in person. Only the person who
 * started the sign-in can finish it, so an authorise URL sent to someone else links nothing.
 */
export const githubLinkFinishSchema = z.object({ ticket: z.string().min(16).max(200) });
export const sourceIdentityLinkSchema = z.object({
    token: z.string().min(8).max(4000),
    connectionId: z.string().optional(),
    /** Jira: the Atlassian account email the API token belongs to. */
    email: z.string().email().max(320).optional(),
});
export const sourceRepositorySchema = z.object({
    /** `owner/name` on GitHub; the repository name on Azure DevOps. */
    name: z.string().min(1).max(300),
    /** The clone URL (https). */
    remote: z.string().min(1).max(500),
    defaultBranch: z.string().min(1).max(200).default("main"),
    /** The repository the project is about; others are libraries or tooling beside it. */
    primary: z.boolean().default(false),
    /** The provider's own id, when known. */
    externalId: z.string().max(100).optional(),
});
export const projectSourceSchema = z.object({
    provider: sourceProviderSchema,
    connectionId: z.string().optional(),
    organisation: z.string().min(1).max(200),
    /** Azure DevOps only: the DevOps project that holds the repositories and work items. */
    devopsProject: z.string().min(1).max(200).optional(),
    repositories: z.array(sourceRepositorySchema).max(100).default([]),
    /** Issue trackers the platform keeps in step with the project's boards (trackers.ts). */
    trackers: z.array(projectTrackerSchema).max(20).default([]),
});
/** One piece of open work at the provider, in one shape for both. */
export const workItemSchema = z.object({
    kind: z.enum(["issue", "pull-request", "work-item"]),
    id: z.string(),
    repository: z.string().optional(),
    title: z.string(),
    state: z.string(),
    url: z.string(),
    author: z.string().optional(),
    assignee: z.string().optional(),
    labels: z.array(z.string()).default([]),
    updatedAt: z.string(),
});
export const gitCredentialRequestSchema = z.object({
    remote: z.string().min(1).max(500),
    /** A node names the person it works for (from the peer token's `act`); a desktop's person is its own. */
    principal: z.string().min(1).max(200).optional(),
});
export const gitCredentialSchema = z.object({
    username: z.string(),
    password: z.string(),
    /** When the caller should ask again; the platform may rotate the connection before then. */
    expiresAt: z.string(),
    repository: z.string(),
    /** Whose credential it is: the person, or `organisation` when the tenant allowed the fallback. */
    as: z.string(),
});
/** `https://github.com/Org/Repo.git`, `git@github.com:Org/Repo.git` and `HTTPS://GITHUB.COM/org/repo/` are one remote. */
export function normaliseRemote(remote) {
    let r = remote.trim();
    const ssh = /^[\w.-]+@([\w.-]+):(.+)$/.exec(r);
    if (ssh)
        r = `https://${ssh[1]}/${ssh[2]}`;
    r = r.replace(/^ssh:\/\/(?:[\w.-]+@)?/, "https://").replace(/^git:\/\//, "https://");
    r = r.replace(/^https?:\/\/(?:[^@/]+@)?/, "https://");
    r = r.replace(/\.git$/i, "").replace(/\/+$/, "");
    const m = /^https:\/\/([^/]+)(\/.*)?$/.exec(r);
    return m ? `https://${m[1].toLowerCase()}${(m[2] ?? "").toLowerCase()}` : r.toLowerCase();
}
/** The repository in a project's source that a root's remote belongs to. */
export function repositoryForRemote(source, remote) {
    if (!source || !remote)
        return undefined;
    const key = normaliseRemote(remote);
    return source.repositories.find((r) => normaliseRemote(r.remote) === key);
}
//# sourceMappingURL=source.js.map